
import { useState, useEffect, useCallback } from 'react';
import {
    Edit,
    TextInput,
    DateInput,
    NumberInput,
    BooleanInput,
    ReferenceInput,
    ReferenceField,
    TextField,
    SelectInput,
    ArrayInput,
    ArrayField,
    Datagrid,
    SimpleFormIterator,
    AutocompleteInput,
    SimpleForm,
    SaveButton,
    DeleteButton,
    useGetList,
    Loading,
    required,
} from 'react-admin';
import { Box, Toolbar, Card, CardContent, Typography, Button } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import OrderCloneButton from './OrderCloneButton';

const apiUrl = `${window.location.protocol}//${window.location.hostname}/api`;

const getHeaders = () => ({
    'x-access-token': localStorage.getItem('token')
});

const rateFormatter = value => {
    console.log('rateFormatter', value);
    if (value == null || value === '$' || value === '') return '$';
    if (isNaN(value)) return;
    const d = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
    return d;
};

const rateParser = value => {
    console.log('rateParser', value);
    return value.replace("$", "");
};

function compare( a, b ) {
  if (a.Delivery_Location < b.Delivery_Location) {
    return -1;
  }
  if (a.Delivery_Location > b.Delivery_Location) {
    return 1;
  }
  return 0;
}

const DeliveryLocationInput = props => {
    const customerID = useWatch({ name: 'CustomerID' });
    const { data, isLoading, error } = useGetList(
        'deliverylocations',
        {
            pagination: { page: 1, perPage: 400 },
            sort: { field: 'Delivery_Location', order: 'ASC' },
            filter: { cid: customerID }
        }
    );
    if (isLoading) { return <Loading />; }
    if (error) { return <p>ERROR</p>; }
    const choices = data ? data.sort(compare) : [];
    return (
        <SelectInput
            choices={choices}
            {...props}
        />
    );
};

const OrderEditToolbar = props => (
    <Toolbar {...props}>
        <Box display="flex" justifyContent="space-between" width="100%">
            <SaveButton redirect="show" />
            <DeleteButton />
        </Box>
    </Toolbar>
);

const OrderEditForm = props => {
    return (
        <SimpleForm
            {...props}
            toolbar={<OrderEditToolbar />}
        >
            <Box display="flex" justifyContent="space-between" width="100%" sx={{ flexWrap: 'wrap', gap: '4px' }}>
                <SaveButton
                    redirect="show"
                    sx={{
                        marginRight: '5px',
                        marginTop: '0px',
                        marginBottom: '0px'
                    }}
                />
                <Box display="flex" sx={{ gap: '4px' }}>
                    <SaveButton
                        variant="outlined"
                        label="Generate PDFs"
                        redirect="show"
                        alwaysEnable
                        sx={{
                            marginTop: '0px',
                            marginBottom: '0px'
                        }}
                    />
                    <OrderCloneButton
                        variant="outlined"
                        sx={{
                            marginTop: '0px',
                            marginBottom: '0px'
                        }}
                    />
                </Box>
            </Box>
            <Box sx={{ padding: { xs: '0.5em', md: '1em' } }}>
                        <Box display="flex">
                            <Box flex={1} display="flex" flexDirection="row" alignItems="flex-start" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: { xs: '4px', md: 0 } }}>
                                <Box sx={{ width: { xs: '100%', md: '300px' }, minWidth: { xs: 'auto', md: '300px' }, maxWidth: { xs: '100%', md: '450px' }, flexShrink: 0 }}>
                                    <ReferenceInput
                                        label="Customer"
                                        source="CustomerID"
                                        reference="customers"
                                        sort={{ field: 'Customer', order: 'ASC' }}
                                        fullWidth={false}
                                        allowEmpty >
                                        <AutocompleteInput
                                            label="Customer"
                                            optionText="Customer"
                                            TextFieldProps={{ sx: { margin: '0px 0px 4px' } }}
                                        />
                                    </ReferenceInput>
                                </Box>
                                <DateInput label="Order Date" source="Order_Date" validate={[required()]} sx={{ width: { xs: '100%', md: '180px' }, minWidth: { xs: 'auto', md: '180px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }}/>
                                <TextInput label="Order Number" source="Order_Num" validate={[required()]} sx={{ width: { xs: '100%', md: '150px' }, minWidth: { xs: 'auto', md: '150px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                <SelectInput label="Type" source="Order_Type" choices={[
                                    { id: 'Packaged', name: 'Packaged' },
                                    { id: 'Bulk', name: 'Bulk' },
                                ]} validate={[required()]} sx={{ flex: { xs: '0 0 100%', md: '1 1 auto' }, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                <TextInput label="PO Number" source="PO_Num" sx={{ flex: { xs: '0 0 100%', md: '1 1 auto' }, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                            </Box>
                        </Box>
                        <ArrayInput label="Order Details" source="OrderDetails" >
                            <SimpleFormIterator inline sx={{ '& .RaSimpleFormIterator-form': { display: 'flex', flexDirection: 'column', gap: '8px' } }}>
                                <Box display="flex" flexDirection="row" gap="8px" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                                    <NumberInput label="Quantity" source="Quantity" sx={{ width: { xs: '100%', md: '80px' }, minWidth: { xs: 'auto', md: '80px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                    <ReferenceInput
                                        label="Precision"
                                        source="PrecisionID"
                                        reference="precisions"
                                        sx={{ width: { xs: '100%', md: '150px' }, minWidth: { xs: 'auto', md: '150px' }, flexShrink: 0 }}
                                        fullWidth={false}
                                        allowEmpty >
                                        <AutocompleteInput label="Precision" optionText="Precision" TextFieldProps={{ sx: { margin: '0px 0px 4px' } }} />
                                    </ReferenceInput>
                                    <Box sx={{ width: { xs: '100%', md: '400px' }, minWidth: { xs: 'auto', md: '400px' }, flexShrink: 0 }}>
                                        <ReferenceInput
                                            label="Product"
                                            source="ProductID"
                                            reference="products"
                                            sort={{ field: 'Product', order: 'ASC' }}
                                            fullWidth={false}
                                            allowEmpty >
                                            <AutocompleteInput label="Product" optionText="Product" TextFieldProps={{ sx: { margin: '0px 0px 4px' } }} />
                                        </ReferenceInput>
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', md: '400px' }, minWidth: { xs: 'auto', md: '250px' }, flexShrink: 0 }}>
                                        <ReferenceInput
                                            label="Pick-Up Location"
                                            source="PickupLocationID"
                                            reference="pickuplocations"
                                            sort={{ field: 'Pickup_Location', order: 'ASC' }}
                                            fullWidth={false}
                                            allowEmpty >
                                            <AutocompleteInput label="Pick-Up Location" optionText="Pickup_Location" TextFieldProps={{ sx: { margin: '0px 0px 4px' } }} />
                                        </ReferenceInput>
                                    </Box>
                                </Box>
                                <Box display="flex" flexDirection="row" gap="8px" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                                    <DeliveryLocationInput
                                        source="DeliveryLocationID"
                                        validate={[required()]}
                                        optionText="Delivery_Location"
                                        label="Delivery Location"
                                        sx={{ width: { xs: '100%', md: '400px' }, minWidth: { xs: 'auto', md: '400px' }, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }}
                                    />
                                    <TextInput label="Release #" source="Release_Num" validate={[required()]} sx={{ width: { xs: '100%', md: '120px' }, minWidth: { xs: 'auto', md: '120px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                </Box>
                            </SimpleFormIterator>
                        </ArrayInput>
                        <Box display="flex">
                            <Box display="flex" flexGrow={1} m="0" p="0" justifyContent="flex-start" flexDirection="row" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: { xs: '4px', md: 0 } }}>
                                <Box sx={{ width: { xs: '100%', md: '300px' }, minWidth: { xs: 'auto', md: '300px' }, maxWidth: { xs: '100%', md: '500px' }, flexShrink: 0 }}>
                                    <ReferenceInput
                                        label="Trucking Company"
                                        source="CarrierID"
                                        reference="truckingcompanies"
                                        sort={{ field: 'Trucking_Company', order: 'ASC' }}
                                        perPage={200}
                                        fullWidth={false}
                                        allowEmpty>
                                        <AutocompleteInput label="Trucking Company" optionText="Trucking_Company" TextFieldProps={{ sx: { margin: '0px 0px 4px' } }} />
                                    </ReferenceInput>
                                </Box>
                                <SelectInput
                                    sx={{ flex: { xs: '0 0 100%', md: '0 0 150px' }, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }}
                                    allowEmpty
                                    label="Truck Type"
                                    source="Truck_Type"
                                    choices={[
                                        { id: 'Doubles', name: 'Doubles' },
                                        { id: 'End-Dump', name: 'End-Dump' },
                                        { id: 'Flatbed', name: 'Flatbed' },
                                        { id: 'Lift-Gate', name: 'Lift-Gate' },
                                        { id: 'Roll-Off', name: 'Roll-Off' },
                                        { id: 'Transfers', name: 'Transfers' },
                                        { id: 'Van', name: 'Van' },
                                        { id: 'Walking Floor', name: 'Walking Floor' },
                                    ]}
                                />
                                <TextInput label="Freight Charge" source="Trucking_Charge" sx={{ width: { xs: '100%', md: '120px' }, minWidth: { xs: 'auto', md: '120px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} format={rateFormatter} parse={rateParser} />
                            </Box>
                        </Box>
                        <Box display="flex">
                            <Box flexGrow={1} m="0" p="0">
                                <Box m="0" p="0" display="flex" flexDirection="row" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, rowGap: { xs: '4px', md: 0 } }}>
                                    <DateInput label="Pick-Up Date" source="PickUp_Date" validate={[required()]} sx={{ width: { xs: '48%', md: '180px' }, minWidth: { xs: 'auto', md: '180px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                    <DateInput label="Delivery Date" source="Delivery_Date" validate={[required()]} sx={{ width: { xs: '48%', md: '180px' }, minWidth: { xs: 'auto', md: '180px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                    <NumberInput label="QTY Loaded" source="Qty_Loaded" step={1} parse={v => v === '' ? null : v} sx={{ width: { xs: '48%', md: '120px' }, minWidth: { xs: 'auto', md: '120px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                    <TextInput label="Invoice" source="Invoice_Num" sx={{ width: { xs: '48%', md: '100px' }, minWidth: { xs: 'auto', md: '100px' }, flexShrink: 0, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} inputProps={{ maxLength: 6 }} />
                                </Box>
                                <Box m="0" p="0">
                                    <BooleanInput
                                        label="Early Delivery"
                                        source="Early_Delivery"
                                        sx={{
                                            order: 2,
                                            marginTop: '0px',
                                            marginBottom: '0px',
                                            padding: '0px 4px 0px 2px;',
                                            display: 'inline',
                                            float: 'left'
                                        }}
                                    />
                                    <BooleanInput
                                        label="Canceled"
                                        source="Canceled"
                                        sx={{
                                            order: 2,
                                            marginTop: '0px',
                                            marginBottom: '0px',
                                            padding: '0px 4px 0px 2px;',
                                            display: 'inline',
                                            float: 'left'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box display="flex">
                            <Box flexGrow={1} m="0" p="0">
                                <Box m="0" p="0" display="flex" flexDirection="row" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                                    <TextInput multiline label="Special Instructions" source="Special_Instructions" sx={{ flex: { xs: '0 0 100%', md: '1 1 auto' }, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }}/>
                                    <TextInput multiline label="Billing Notes" source="Billing_Notes" sx={{ flex: { xs: '0 0 100%', md: '1 1 auto' }, marginTop: '0px', marginBottom: '0px', padding: '0px 4px 0px 2px;' }} />
                                </Box>
                                <Box m="0" p="0">
                                    <BooleanInput
                                        label="Pending"
                                        source="Pending"
                                        sx={{
                                            order: 2,
                                            marginTop: '0px',
                                            marginBottom: '0px',
                                            padding: '0px 4px 0px 2px;',
                                            display: 'inline',
                                            float: 'left'
                                        }}
                                    />
                                    <BooleanInput
                                        label="Emailed PickUp Notice"
                                        source="Emailed_PickUp_Notice"
                                        sx={{
                                            order: 2,
                                            marginTop: '0px',
                                            marginBottom: '0px',
                                            padding: '0px 4px 0px 2px;',
                                            display: 'inline',
                                            float: 'left'
                                        }}
                                    />
                                    <BooleanInput
                                        label="Emailed Trucking Manifest"
                                        source="Emailed_Trucking_Manifest"
                                        sx={{
                                            order: 2,
                                            marginTop: '0px',
                                            marginBottom: '0px',
                                            padding: '0px 4px 0px 2px;',
                                            display: 'inline',
                                            float: 'left'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box flexGrow={1} m="20" p="0">
                        <ArrayField source="OrderDetails">
                            <Datagrid bulkActionButtons={false}>
                                <ReferenceField label="Edit Delivery Location" source="DeliveryLocationID" reference="deliverylocations" sortable={false} link="edit">
                                    <TextField source="Delivery_Location" />
                                </ReferenceField>
                            </Datagrid>
                        </ArrayField>
                    </Box>
        </SimpleForm>
    )
}

export const OrderEdit = props => {
    const { id } = useParams();
    const [lockStatus, setLockStatus] = useState('checking');
    const [lockedBy, setLockedBy] = useState(null);

    const tryAcquireLock = useCallback(async () => {
        setLockStatus('checking');
        try {
            const response = await fetch(`${apiUrl}/ordereditlock/${id}`, {
                method: 'POST',
                headers: getHeaders()
            });
            const data = await response.json();
            if (response.status === 409) {
                setLockStatus('locked');
                setLockedBy(data.locked_by || 'Another user');
            } else {
                setLockStatus('acquired');
                setLockedBy(data.locked_by || null);
            }
        } catch (error) {
            console.error('Lock check failed:', error);
            setLockStatus('acquired');
        }
    }, [id]);

    useEffect(() => {
        if (!id) return;
        tryAcquireLock();

        const heartbeat = setInterval(() => {
            fetch(`${apiUrl}/ordereditlock/${id}`, {
                method: 'POST',
                headers: getHeaders()
            }).catch(console.error);
        }, 120000);

        return () => {
            clearInterval(heartbeat);
            fetch(`${apiUrl}/ordereditlock/${id}`, {
                method: 'DELETE',
                headers: getHeaders(),
                keepalive: true
            }).catch(console.error);
        };
    }, [id, tryAcquireLock]);

    if (lockStatus === 'checking') {
        return <Loading />;
    }

    if (lockStatus === 'locked') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Card sx={{ maxWidth: 450, textAlign: 'center' }}>
                    <CardContent sx={{ p: 4 }}>
                        <LockIcon sx={{ fontSize: 48, color: '#d32f2f', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Order Locked
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                            <strong>{lockedBy}</strong> is currently editing this order.
                            Please try again in a few minutes.
                        </Typography>
                        <Button variant="contained" onClick={tryAcquireLock}>
                            Try Again
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Edit {...props} redirect="show">
            {lockedBy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, backgroundColor: '#e8f5e9', borderBottom: '1px solid #c8e6c9' }}>
                    <PersonIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                    <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                        Active user: <strong>{lockedBy}</strong>
                    </Typography>
                </Box>
            )}
            <OrderEditForm />
        </Edit>
    );
};

export default OrderEdit;
